import type { LocationQuery } from "vue-router";
import dayjs from "dayjs";
import { getDateRange, type DateRange } from "@lemmonhq/uikit";

/** An in-app tab is a report instance (not a definition) — two tabs can share a definition. */
export type DataRoomTab = {
  id: string;
  definitionId: string | null;
  parameters: Record<string, unknown>;
  title?: string;
};

/** Minimal shape the frontend needs from a catalog entry. */
export type CatalogEntry = {
  definitionId: string;
  definitionVersion: string;
  nameKey: string;
  descriptionKey: string;
  group: string;
  icon: string;
  parameters: { properties?: Record<string, { title?: string }> } | unknown;
  interactive: {
    summary: { metricsPath: string; metrics: Array<any> };
    chart: any;
    table: { rowKey: string; columns: Array<any>; hierarchy?: any };
  };
  presentations: Array<{ id: string; kind: "pdf" | "xlsx" }>;
};

export function newTabId(): string {
  return crypto.randomUUID();
}

/**
 * Breakdown values the report definitions accept. Kept in one place because the
 * URL round-trip allowlists below must not drift from the server-side union —
 * a retired value reaching tRPC fails validation for the whole report. Values
 * absent from this list are dropped when parsing, so stale links degrade to the
 * definition's default instead of erroring.
 */
const BREAKDOWN_VALUES = [
  "discount-product",
  "staff-product",
  "product-staff",
  "product-selection",
  "selection-product",
] as const;

const isBreakdown = (value: unknown): value is (typeof BREAKDOWN_VALUES)[number] =>
  typeof value === "string" &&
  (BREAKDOWN_VALUES as readonly string[]).includes(value);

/** Whole-day window, used when the restaurant has no opening hours configured. */
export const FULL_DAY_WINDOW = { startTime: "00:00", endTime: "23:59" };

export type ReportTimeWindow = { startTime: string; endTime: string };

/**
 * True when a window closes on the day after it opens — a `07:00 → 06:59`
 * business day. Mirrors the rule `order_processing_get_shift_by_time` uses in
 * SQL: an `end_time` before `start_time` spans midnight.
 */
export const windowSpansMidnight = (window: ReportTimeWindow): boolean =>
  window.endTime < window.startTime;

const shiftDate = (date: string, days: number): string =>
  dayjs(date).add(days, "day").format("YYYY-MM-DD");

/**
 * `from`/`to` are full timestamps because Data Room reports are time-aware.
 * The end is pushed to the end of its minute so an `endTime` of `23:59` still
 * means "through the end of the day", exactly as the old hardcoded
 * `T23:59:59.999999` did.
 *
 * A window that spans midnight closes on the *following* calendar day, so the
 * end bound advances with it. Without that, a restaurant whose opening hours
 * run `07:00 → 06:59` produced `to` before `from` on any single-day range and
 * the server rejected the report with "from must be on or before to".
 */
export function toReportBounds(
  startDate: string,
  endDate: string,
  window: ReportTimeWindow = FULL_DAY_WINDOW,
): { from: string; to: string } {
  const closingDate = windowSpansMidnight(window)
    ? shiftDate(endDate, 1)
    : endDate;
  return {
    from: `${startDate}T${window.startTime}:00`,
    to: `${closingDate}T${window.endTime}:59.999999`,
  };
}

/** Pull the `HH:mm` back out of a `from`/`to` timestamp, if it carries one. */
export function timeFromBound(bound: unknown, fallback: string): string {
  const match = /T(\d{2}:\d{2})/.exec(String(bound ?? ""));
  return match ? match[1] : fallback;
}

/**
 * The last calendar day a bounds pair covers, undoing the extra day
 * `toReportBounds` adds for a midnight-spanning window. The window is read back
 * off the bounds themselves rather than from restaurant settings, because a tab
 * may carry a user override that does not span midnight.
 */
export function endDateFromBounds(from: unknown, to: unknown): string {
  const date = String(to ?? "").slice(0, 10);
  const startTime = timeFromBound(from, "");
  const endTime = timeFromBound(to, "");
  if (!date || !startTime || !endTime || endTime >= startTime) return date;
  return shiftDate(date, -1);
}

/**
 * Default parameters for a freshly-selected report: no filters, over the
 * restaurant's configured opening hours.
 *
 * `range` is the range shared with the other report pages, so a report opened
 * here starts on the period the user is already looking at elsewhere; without
 * one it falls back to this month. Times carried on the range win over
 * `window`, which is only the fallback for a range that has none.
 */
export function defaultParameters(
  window: ReportTimeWindow = FULL_DAY_WINDOW,
  range?: DateRange,
): Record<string, unknown> {
  const r: DateRange = range ?? {
    type: "this_month",
    ...getDateRange("this_month"),
  };
  return toReportBounds(r.startDate, r.endDate, {
    startTime: r.startTime ?? window.startTime,
    endTime: r.endTime ?? window.endTime,
  });
}

/** Which shared filter controls a definition declares, inferred from schema property titles. */
export function declaredControls(entry: CatalogEntry): Set<string> {
  const controls = new Set<string>();
  const schema = entry.parameters as {
    properties?: Record<string, { title?: string }>;
  };
  const props = schema?.properties ?? {};
  for (const prop of Object.values(props)) {
    if (prop?.title) controls.add(prop.title);
  }
  // TypeBox Intersect schemas nest properties under allOf.
  const allOf = (entry.parameters as any)?.allOf as Array<any> | undefined;
  if (allOf) {
    for (const part of allOf) {
      for (const prop of Object.values(part?.properties ?? {})) {
        if ((prop as any)?.title) controls.add((prop as any).title);
      }
    }
  }
  return controls;
}

/** Serialize the active view (definition + params) into URL query. Multi-selects repeat. */
export function paramsToQuery(
  definitionId: string,
  parameters: Record<string, unknown>,
): LocationQuery {
  const query: LocationQuery = { report: definitionId };
  if (typeof parameters.from === "string") query.from = parameters.from;
  if (typeof parameters.to === "string") query.to = parameters.to;
  if (
    Array.isArray(parameters.paymentMethods) &&
    parameters.paymentMethods.length
  )
    query.paymentMethod = parameters.paymentMethods.map(String);
  if (Array.isArray(parameters.userIds) && parameters.userIds.length)
    query.userId = parameters.userIds.map(String);
  if (
    Array.isArray(parameters.serviceMethods) &&
    parameters.serviceMethods.length
  )
    query.serviceMethod = parameters.serviceMethods.map(String);
  if (isBreakdown(parameters.breakdown)) query.breakdown = parameters.breakdown;
  return query;
}

function asArray(v: unknown): string[] {
  if (v == null) return [];
  return Array.isArray(v) ? v.map(String) : [String(v)];
}

/** Parse known query params back into a report parameter object. */
export function queryToParams(
  query: LocationQuery,
  window: ReportTimeWindow = FULL_DAY_WINDOW,
  range?: DateRange,
): {
  definitionId: string | null;
  parameters: Record<string, unknown>;
} {
  const definitionId = typeof query.report === "string" ? query.report : null;
  const parameters: Record<string, unknown> = {};
  if (typeof query.from === "string") parameters.from = query.from;
  if (typeof query.to === "string") parameters.to = query.to;

  const paymentMethods = asArray(query.paymentMethod);
  if (paymentMethods.length) parameters.paymentMethods = paymentMethods;
  const serviceMethods = asArray(query.serviceMethod);
  if (serviceMethods.length) parameters.serviceMethods = serviceMethods;
  const userIds = asArray(query.userId)
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
  if (userIds.length) parameters.userIds = userIds;
  if (isBreakdown(query.breakdown)) parameters.breakdown = query.breakdown;

  if (!parameters.from || !parameters.to) {
    const d = defaultParameters(window, range);
    parameters.from ??= d.from;
    parameters.to ??= d.to;
  }
  return { definitionId, parameters };
}

/** Stable key for Vue Query — parameters normalized to a deterministic string. */
export function normalizedKey(parameters: Record<string, unknown>): string {
  return JSON.stringify(parameters, Object.keys(parameters).sort());
}
